import type { Message } from 'ably';
import type { RealtimeChannel } from 'ably';

/** Distinct aggregation; `name` values `read` / `deleted` per notification message serial. */
export const NOTIFICATION_ANNOTATION_DISTINCT_TYPE = 'notification:distinct.v1';

type SummaryClientList = { clientIds?: string[] };

export function applyAnnotationSummaryFromMessage<T extends { read?: boolean; dismissed: boolean }>(
  message: Message,
  n: T,
  userId: number | undefined
): T {
  if (userId === undefined) return n;
  const summary = message.annotations?.summary as Record<string, unknown> | undefined;
  if (!summary || typeof summary !== 'object') return n;

  const bucket = summary[NOTIFICATION_ANNOTATION_DISTINCT_TYPE] as Record<string, SummaryClientList> | undefined;
  if (!bucket || typeof bucket !== 'object') return n;

  const uid = String(userId);
  const deletedEntry = bucket.deleted;

  const {read} = n;
  let {dismissed} = n;

  // Do not set `read` from message annotation summary: on channel attach/rewind it can mark
  // items read before the user clicks them in the panel. `read` comes from UI + annotation events only.
  if (deletedEntry?.clientIds?.includes(uid)) dismissed = true;

  if (dismissed === n.dismissed) return n;
  return { ...n, dismissed };
}

export async function publishDistinctAnnotation(
  channel: RealtimeChannel,
  messageSerial: string,
  name: 'read' | 'deleted'
): Promise<void> {
  await channel.annotations.publish(messageSerial, {
    type: NOTIFICATION_ANNOTATION_DISTINCT_TYPE,
    name,
  });
}
